import classNames from "classnames";
import { Plus, X } from "lucide-react";
import * as React from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  type FieldPath,
} from "react-hook-form";

import {
  Card,
  Field,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";
import textStyles from "@/shared/foundation/styles/textStyles.module.css";

import { type AgentFormValues } from "../agentFormSchema";

import styles from "./goals.module.css";

/** The three list-of-pairs a goal can carry, addressed by path. */
export type VocabularyName =
  | `goals.${number}.label_vocabulary`
  | `goals.${number}.event_vocabulary`
  | `goals.${number}.tag_vocabulary`;

export interface VocabularyBlockProps {
  name: VocabularyName;
  /** The row field holding the key: each vocabulary names it differently. */
  keyField: "label" | "name" | "tag";
  title: string;
  hint: React.ReactNode;
  /** Heading over the key column. */
  keyHeading: string;
  keyPlaceholder: string;
  descriptionHeading: string;
  descriptionPlaceholder: string;
  /** What the button to add a row says. */
  addLabel: string;
  /** Rows this vocabulary may hold, per the schema. */
  max: number;
  testId: string;
}

/**
 * One key-and-description vocabulary: the rows, a way to add another, and
 * whatever the schema says about the list as a whole. Drawn the way the
 * trigger form draws environment variables.
 *
 * All three vocabularies are the same control under different words, so the
 * words are the props and the shape is not repeated three times. Row paths are
 * assembled from `name` and `keyField`, which is more than the path types can
 * follow, so they are asserted once here rather than at each field.
 */
export function VocabularyBlock({
  name,
  keyField,
  title,
  hint,
  keyHeading,
  keyPlaceholder,
  descriptionHeading,
  descriptionPlaceholder,
  addLabel,
  max,
  testId,
}: VocabularyBlockProps) {
  const { control, getFieldState, formState } =
    useFormContext<AgentFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const keyHeadingId = React.useId();
  const descriptionHeadingId = React.useId();
  const listError = getFieldState(name, formState).error;
  const rowPath = (index: number, field: string) =>
    `${name}.${index}.${field}` as FieldPath<AgentFormValues>;

  return (
    <Card.Root size="compact" data-testid={testId} data-count={fields.length}>
      <Card.Header>
        <Card.Title>{title}</Card.Title>
      </Card.Header>
      <Card.Body>
        <p className={styles.hint}>{hint}</p>
        {fields.length > 0 && (
          <div className={styles.grid}>
            <span
              id={keyHeadingId}
              className={classNames(textStyles.overline, styles.heading)}
            >
              {keyHeading}
            </span>
            <span
              id={descriptionHeadingId}
              className={classNames(textStyles.overline, styles.heading)}
            >
              {descriptionHeading}
            </span>
            {fields.map((row, index) => (
              <div
                key={row.id}
                className={styles.row}
                data-testid={`${testId}-${index}`}
              >
                <Controller
                  control={control}
                  name={rowPath(index, keyField)}
                  render={({ field, fieldState }) => (
                    <Field.Root name={field.name} invalid={fieldState.invalid}>
                      <Field.Control
                        render={<TextInput spellCheck={false} />}
                        aria-labelledby={keyHeadingId}
                        placeholder={keyPlaceholder}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                      />
                      <Field.Error match>
                        {fieldState.error?.message}
                      </Field.Error>
                    </Field.Root>
                  )}
                />
                <Controller
                  control={control}
                  name={rowPath(index, "description")}
                  render={({ field, fieldState }) => (
                    <Field.Root name={field.name} invalid={fieldState.invalid}>
                      <Field.Control
                        render={<TextInput />}
                        aria-labelledby={descriptionHeadingId}
                        placeholder={descriptionPlaceholder}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                      />
                      <Field.Error match>
                        {fieldState.error?.message}
                      </Field.Error>
                    </Field.Root>
                  )}
                />
                <RobotoButton
                  variant="ghost"
                  icon={<X aria-hidden />}
                  aria-label={`Remove ${keyHeading.toLowerCase()} ${index + 1}`}
                  onClick={() => {
                    remove(index);
                  }}
                  data-testid={`${testId}-${index}-remove`}
                />
              </div>
            ))}
          </div>
        )}

        <div>
          <RobotoButton
            variant="secondary"
            icon={<Plus aria-hidden size={16} />}
            disabled={fields.length >= max}
            onClick={() => {
              append({ [keyField]: "", description: "" } as never);
            }}
            data-testid={`${testId}-add`}
          >
            {addLabel}
          </RobotoButton>
        </div>

        {listError?.message !== undefined && (
          <Field.Root invalid>
            <Field.Error match>{listError.message}</Field.Error>
          </Field.Root>
        )}
      </Card.Body>
    </Card.Root>
  );
}
